import dayjs from 'dayjs';
import api from '../axiosConfig';

const DEFAULT_ERROR = 'Failed to generate report. Please try again.';
const JOBS_URL = '/api/reporting/reports/quality/jobs';
const POLL_INTERVAL = 1000;

function getHeader(headers, name) {
  if (typeof headers?.get === 'function') return headers.get(name) || '';
  const key = Object.keys(headers || {}).find((value) => value.toLowerCase() === name);
  return key ? headers[key] : '';
}

async function readResponseMessage(data) {
  try {
    let payload = data;
    if (data instanceof Blob) {
      const contents = typeof data.text === 'function'
        ? await data.text()
        : await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsText(data);
        });
      payload = JSON.parse(contents);
    } else if (typeof data === 'string') {
      payload = JSON.parse(data);
    }
    return typeof payload?.message === 'string' && payload.message.trim()
      ? payload.message
      : null;
  } catch (_) {
    return null;
  }
}

export async function getReportErrorMessage(error) {
  return (await readResponseMessage(error?.response?.data)) || error?.reportMessage || DEFAULT_ERROR;
}

export function getReportFilename(disposition, mode) {
  const encoded = disposition?.match(/filename\*\s*=\s*UTF-8'[^']*'([^;]+)/i);
  const plain = disposition?.match(/filename\s*=\s*(?:"([^"]*)"|([^;]+))/i);
  let filename;
  if (encoded) {
    try { filename = decodeURIComponent(encoded[1].trim()); } catch (_) { /* Use the plain filename below. */ }
  }
  filename = filename || plain?.[1] || plain?.[2]?.trim();
  if (filename) {
    // A server filename is a download label, never a path.
    filename = Array.from(filename, (character) => {
      const code = character.charCodeAt(0);
      return character === '/' || character === '\\' || code < 32 || code === 127 ? '_' : character;
    }).join('');
    if (/\.pdf$/i.test(filename)) return filename;
  }
  const label = mode === 'area' ? 'area-report' : mode === 'failed' ? 'failed-items-report' : 'full-report';
  return `${label}-${dayjs().format('YYYYMMDD-HHmm')}.pdf`;
}

function cancelledError() {
  const error = new Error('Report generation cancelled.');
  error.name = 'AbortError';
  return error;
}

function reportError(message) {
  const error = new Error(message);
  error.reportMessage = message;
  return error;
}

function retryDelay(error, attempt) {
  const retryAfter = getHeader(error?.response?.headers, 'retry-after');
  const seconds = Number(retryAfter);
  const specified = retryAfter
    ? (Number.isFinite(seconds) ? seconds * 1000 : Date.parse(retryAfter) - Date.now())
    : NaN;
  return Math.min(30000, Math.max(1000, Number.isFinite(specified) ? specified : 1000 * (2 ** attempt)));
}

export async function downloadInspectionReport({ mode, area, onProgress, signal }) {
  const sessionToken = localStorage.getItem('token');
  const controller = new AbortController();
  let stopped;
  let jobId;
  let cancellation;
  let cancellationFailure;
  let downloaded = false;

  // Every request, including cancellation, keeps its originating register.
  // A revoked old session cannot cancel server work; expiry cleans it up.
  const requestConfig = (cleanup = false) => ({
    headers: sessionToken ? { Authorization: `Bearer ${sessionToken}` } : {},
    timeout: cleanup ? 10000 : 30000,
    ...(cleanup ? {} : { signal: controller.signal }),
    transformRequest: [
      (data, headers) => {
        if (!cleanup) ensureCurrent();
        if (sessionToken) headers.Authorization = `Bearer ${sessionToken}`;
        else delete headers.Authorization;
        return data;
      },
      ...(api.defaults?.transformRequest || []),
    ],
  });
  const cancelJob = () => {
    if (!jobId || downloaded) return undefined;
    if (!cancellation) {
      cancellation = api.delete(`${JOBS_URL}/${encodeURIComponent(jobId)}`, requestConfig(true))
        .catch((error) => { cancellationFailure = error; });
    }
    return cancellation;
  };
  const stop = (reason) => {
    if (!stopped) stopped = reason;
    controller.abort();
    cancelJob();
  };
  function ensureCurrent() {
    if (localStorage.getItem('token') !== sessionToken) {
      stop(reportError('Your account or register changed. Generate the report again in the current register.'));
    }
    if (stopped) throw stopped;
  }
  const abort = () => stop(cancelledError());
  const pause = (duration) => new Promise((resolve, reject) => {
    ensureCurrent();
    const finish = () => {
      controller.signal.removeEventListener('abort', interrupted);
      resolve();
    };
    const timer = window.setTimeout(finish, duration);
    const interrupted = () => {
      window.clearTimeout(timer);
      controller.signal.removeEventListener('abort', interrupted);
      reject(stopped || cancelledError());
    };
    controller.signal.addEventListener('abort', interrupted, { once: true });
  });
  const publish = (job) => {
    ensureCurrent();
    onProgress?.({ ...job, progress: Math.max(0, Math.min(100, Number(job.progress) || 0)) });
  };
  const getWithRetry = async (url, config = {}) => {
    for (let attempt = 0; ; attempt += 1) {
      ensureCurrent();
      try {
        return await api.get(url, { ...requestConfig(), ...config });
      } catch (error) {
        ensureCurrent();
        const status = error?.response?.status;
        const transient = !error?.response || [408, 429, 500, 502, 503, 504].includes(status);
        if (!transient || attempt >= 3) throw error;
        await pause(retryDelay(error, attempt));
      }
    }
  };
  signal?.addEventListener('abort', abort, { once: true });
  if (signal?.aborted) abort();
  // localStorage's storage event does not fire in the tab that changes it.
  const scopeWatcher = window.setInterval(() => {
    try { ensureCurrent(); } catch (_) { /* The pending request/wait observes cancellation. */ }
  }, 250);

  try {
    ensureCurrent();
    publish({ status: 'queued', stage: 'queued', progress: 0 });
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
    // Never retry creation: a lost response could already have started a job.
    // Let an in-flight creation return its ID even after Cancel, so it can be deleted.
    const creationConfig = requestConfig();
    delete creationConfig.signal;
    let job = (await api.post(JOBS_URL, {
      mode, ...(mode === 'area' ? { area } : {}), timezone,
    }, creationConfig)).data;
    jobId = job?.id;
    ensureCurrent();
    if (!jobId) throw reportError('The server did not return a report job. Please try again.');

    while (true) {
      publish(job);
      if (job.status === 'completed') break;
      if (job.status === 'failed') throw reportError(job.message || DEFAULT_ERROR);
      if (job.status === 'cancelled') throw cancelledError();
      if (!['queued', 'running'].includes(job.status)) throw reportError('The server returned an invalid report status. Please try again.');
      await pause(POLL_INTERVAL);
      job = (await getWithRetry(`${JOBS_URL}/${encodeURIComponent(jobId)}`)).data;
      ensureCurrent();
    }

    publish({ ...job, stage: 'downloading', progress: 100 });
    // Large completed PDFs may take several minutes on slower connections.
    // Cancellation remains available through the request's AbortSignal.
    const response = await getWithRetry(`${JOBS_URL}/${encodeURIComponent(jobId)}/pdf`, { responseType: 'blob', timeout: 0 });
    ensureCurrent();
    if (!getHeader(response.headers, 'content-type').toLowerCase().includes('application/pdf')) {
      throw reportError((await readResponseMessage(response.data)) || DEFAULT_ERROR);
    }
    ensureCurrent();
    const blob = response.data instanceof Blob
      ? response.data
      : new Blob([response.data], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = getReportFilename(getHeader(response.headers, 'content-disposition'), mode);
    document.body.appendChild(link);
    try {
      ensureCurrent();
      link.click();
      downloaded = true;
    } finally {
      link.remove();
      // Give the browser time to start consuming the download before releasing it.
      window.setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    }
  } catch (error) {
    await cancelJob();
    if (stopped?.name === 'AbortError' && cancellationFailure) {
      throw reportError('Could not confirm report cancellation. Please try again shortly.');
    }
    throw stopped || error;
  } finally {
    window.clearInterval(scopeWatcher);
    signal?.removeEventListener('abort', abort);
  }
}
